import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { usePageTitleContext } from "./page-title-context";

type Props = {
	/** Label de la sección/pestaña (sin el sufijo de marca, lo añade el template). */
	title: string;
	/** Nombre crudo de la entidad concreta (rootName, p.ej. "207"). Opcional. */
	entityName?: string;
	/**
	 * Prefijo del track para MOSTRAR la entidad en el título visible ("Ladle" /
	 * "Tundish"). No afecta al `entityName` crudo que guarda el contexto para los
	 * modales.
	 */
	entityPrefix?: string;
	/**
	 * No registrar este título como "página activa" en el contexto (sólo aplica el
	 * <title> con Helmet). Úsalo en vistas anidadas/profundas (p.ej. editar un task
	 * group dentro de Ladle Details): Helmet ya muestra el título más profundo en
	 * la pestaña, pero el contexto debe seguir reflejando la sección de página para
	 * que los modales compongan "<acción> · <sección>".
	 */
	skipContext?: boolean;
};

/**
 * Declara el título de pestaña de la página actual. Cada página lo monta con su
 * propio texto (ya no se deriva de la URL). El título visible es
 * `<Entidad> · <Sección>` (con la entidad prefijada por el track); el contexto
 * guarda la sección y el `entityName` crudo para que los modales compongan su
 * propio título.
 */
export const PageTitle = ({
	title,
	entityName,
	entityPrefix,
	skipContext,
}: Props) => {
	const { setPageTitle } = usePageTitleContext();
	useEffect(() => {
		if (skipContext) return;
		setPageTitle({ title, entityName });
	}, [title, entityName, skipContext, setPageTitle]);
	const entityDisplay = entityName
		? [entityPrefix, entityName].filter(Boolean).join(" ")
		: undefined;
	const visible = entityDisplay ? `${entityDisplay} · ${title}` : title;
	return <Helmet title={visible} />;
};
